import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { externalCostsService } from '../services/externalCostsService';
import toast from 'react-hot-toast';
import api from '../services/api';
import { ZKsefTab, PodsumowanieMiesieczneTab, OznaczZaplaconyModal, ZRODLA, formatujDate, dniPoTerminie, dzisISO, skrocRachunek, formatujRachunek } from '../components/koszty';

// Konfiguracja statusow
const statusConfig = {
  planowany: { bg: 'var(--border)', text: 'var(--text)', label: 'Planowany' },
  niezaplacony: { bg: 'var(--warning-light)', text: 'var(--warning)', label: 'Do zaplaty' },
  zaplacony: { bg: 'var(--success-light)', text: 'var(--success)', label: 'Zaplacony' }
};

// Konfiguracja cykli
const cyklConfig = {
  miesiac: { label: 'Miesieczny', icon: '📅' },
  kwartal: { label: 'Kwartalny', icon: '📆' },
  rok: { label: 'Roczny', icon: '🗓️' }
};

export default function ExternalCostsPage() {
  const { selectedLocation, locationId } = useLocation() || {};
  const [activeTab, setActiveTab] = useState('costs');
  const [loading, setLoading] = useState(true);

  // Dane
  const [costs, setCosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [pendingRecurring, setPendingRecurring] = useState([]);
  const [locations, setLocations] = useState([]);

  // Filtry
  const [filters, setFilters] = useState({
    location_id: '',
    category_id: '',
    status: '',
    date_from: '',
    date_to: '',
    nip: '',
    zrodlo: '',
    przeterminowane: false,
    page: 1,
    per_page: 30
  });

  // Etap 2: koszty z KSeF - licznik do odznaki na zakladce, modal "oznacz zaplacony" (KW albo konto firmowe)
  const [ileZKsef, setIleZKsef] = useState(null);
  const [kosztDoZaplaty, setKosztDoZaplaty] = useState(null);

  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  // Modal dodawania/edycji
  const [showModal, setShowModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [formData, setFormData] = useState({
    location_id: '',
    category_id: '',
    nazwa: '',
    opis: '',
    kwota_netto: '',
    stawka_vat: 23,
    data_kosztu: new Date().toISOString().split('T')[0],
    okres_od: '',
    okres_do: '',
    cykliczny: false,
    cykl_typ: '',
    kontrahent: '',
    numer_dokumentu: ''
  });

  // Modal kategorii
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ nazwa: '', typ: 'staly', ikona: 'folder', kolor: 'var(--text-secondary)' });

  // Pobierz lokalizacje
  const fetchLocations = useCallback(async () => {
    try {
      const response = await api.get('/locations');
      const data = response.data;
      if (data.success) {
        setLocations(data.data || []);
      }
    } catch (error) {
      console.error('Blad pobierania lokalizacji:', error);
    }
  }, []);

  // Pobierz kategorie
  const fetchCategories = useCallback(async () => {
    try {
      const response = await externalCostsService.getCategories();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error('Blad pobierania kategorii:', error);
    }
  }, []);

  // Pobierz koszty
  const fetchCosts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await externalCostsService.getCosts(filters);
      if (response.success) {
        setCosts(response.data?.items || []);
        setPagination(response.data?.pagination || { total: 0, pages: 1 });
      }
    } catch (error) {
      console.error('Blad pobierania kosztow:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Pobierz statystyki
  const fetchStats = useCallback(async () => {
    try {
      const response = await externalCostsService.getStats({
        location_id: filters.location_id,
        date_from: filters.date_from,
        date_to: filters.date_to
      });
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Blad pobierania statystyk:', error);
    }
  }, [filters.location_id, filters.date_from, filters.date_to]);

  // Pobierz koszty cykliczne do wygenerowania
  const fetchPendingRecurring = useCallback(async () => {
    try {
      const response = await externalCostsService.getPendingRecurring();
      if (response.success) {
        setPendingRecurring(response.data || []);
      }
    } catch (error) {
      console.error('Blad pobierania kosztow cyklicznych:', error);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchCategories();
    externalCostsService.getZKsef({ na_strone: 1 }).then(r => setIleZKsef(r?.data?.razem ?? 0)).catch(() => {});
  }, [fetchLocations, fetchCategories]);

  useEffect(() => {
    fetchCosts();
    fetchStats();
    fetchPendingRecurring();
  }, [fetchCosts, fetchStats, fetchPendingRecurring]);

  // Zapisz koszt
  const handleSaveCost = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        location_id: formData.location_id || null,
        category_id: parseInt(formData.category_id),
        kwota_netto: parseFloat(formData.kwota_netto),
        stawka_vat: parseInt(formData.stawka_vat)
      };

      if (editingCost) {
        await externalCostsService.updateCost(editingCost.id, dataToSend);
      } else {
        await externalCostsService.createCost(dataToSend);
      }

      setShowModal(false);
      setEditingCost(null);
      resetForm();
      fetchCosts();
      fetchStats();
    } catch (error) {
      toast.error('Błąd zapisywania kosztu: ' + error.message);
    }
  };

  // Oznacz jako zaplacony: modal z wyborem KW z kasy sklepu (jak dotad) albo konto firmowe bez KW
  const handlePayCost = (cost) => setKosztDoZaplaty(cost);

  // Usun koszt
  const handleDeleteCost = async (cost) => {
    if (!window.confirm(`Usunac koszt "${cost.nazwa}"?`)) return;
    try {
      await externalCostsService.deleteCost(cost.id);
      fetchCosts();
      fetchStats();
    } catch (error) {
      toast.error('Błąd: ' + error.message);
    }
  };

  // Generuj koszty cykliczne
  const handleGenerateRecurring = async () => {
    if (!window.confirm('Wygenerowac wszystkie zaległe koszty cykliczne?')) return;
    try {
      const response = await externalCostsService.generateRecurring();
      if (response.success) {
        toast.success(`Wygenerowano ${response.data.generated} kosztów`);
        fetchCosts();
        fetchStats();
        fetchPendingRecurring();
      }
    } catch (error) {
      toast.error('Błąd: ' + error.message);
    }
  };

  // Zapisz kategorie
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      await externalCostsService.createCategory(categoryForm);
      setShowCategoryModal(false);
      setCategoryForm({ nazwa: '', typ: 'staly', ikona: 'folder', kolor: 'var(--text-secondary)' });
      fetchCategories();
    } catch (error) {
      toast.error('Błąd: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      location_id: '',
      category_id: '',
      nazwa: '',
      opis: '',
      kwota_netto: '',
      stawka_vat: 23,
      data_kosztu: new Date().toISOString().split('T')[0],
      okres_od: '',
      okres_do: '',
      cykliczny: false,
      cykl_typ: '',
      kontrahent: '',
      numer_dokumentu: ''
    });
  };

  const openEditModal = (cost) => {
    setEditingCost(cost);
    setFormData({
      location_id: cost.location_id || '',
      category_id: cost.category_id,
      nazwa: cost.nazwa,
      opis: cost.opis || '',
      kwota_netto: cost.kwota_netto,
      stawka_vat: cost.stawka_vat,
      data_kosztu: cost.data_kosztu,
      okres_od: cost.okres_od || '',
      okres_do: cost.okres_do || '',
      cykliczny: cost.cykliczny,
      cykl_typ: cost.cykl_typ || '',
      kontrahent: cost.kontrahent || '',
      numer_dokumentu: cost.numer_dokumentu || ''
    });
    setShowModal(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(value || 0);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>Koszty zewnetrzne</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {pendingRecurring.length > 0 && (
            <button
              onClick={handleGenerateRecurring}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: 'var(--warning)',
                color: '#000'
              }}
            >
              Generuj cykliczne ({pendingRecurring.length})
            </button>
          )}
          <button
            onClick={() => { resetForm(); setEditingCost(null); setShowModal(true); }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid var(--success)', color: 'var(--success)'
            }}
          >
            + Dodaj koszt
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid var(--border)', paddingBottom: '10px' }}>
        {[
          { id: 'costs', label: 'Lista kosztow', icon: '📋' },
          { id: 'z-ksef', label: `Z KSeF do zaksięgowania${ileZKsef !== null ? ` (${ileZKsef})` : ''}`, icon: '🧾' },
          { id: 'miesiecznie', label: 'Podsumowanie miesięczne', icon: '📅' },
          { id: 'stats', label: 'Statystyki', icon: '📊' },
          { id: 'categories', label: 'Kategorie', icon: '🏷️' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: activeTab === tab.id ? 'var(--primary)' : 'var(--card-bg)',
              border: activeTab === tab.id ? '1px solid var(--primary)' : '1px solid var(--border)',
              color: activeTab === tab.id ? '#fff' : 'var(--text-secondary)',
              fontWeight: activeTab === tab.id ? '600' : '500'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Filtry */}
      {activeTab === 'costs' && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', background: 'var(--bg)', padding: '15px', borderRadius: '8px' }}>
          <select
            value={filters.location_id}
            onChange={(e) => setFilters({ ...filters, location_id: e.target.value, page: 1 })}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px' }}
          >
            <option value="">Wszystkie lokalizacje</option>
            <option value="0">Koszty ogolne firmy</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.nazwa}</option>
            ))}
          </select>

          <select
            value={filters.category_id}
            onChange={(e) => setFilters({ ...filters, category_id: e.target.value, page: 1 })}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px' }}
          >
            <option value="">Wszystkie kategorie</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.nazwa}</option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px' }}
          >
            <option value="">Wszystkie statusy</option>
            <option value="planowany">Planowany</option>
            <option value="niezaplacony">Do zaplaty</option>
            <option value="zaplacony">Zaplacony</option>
          </select>

          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value, page: 1 })}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px' }}
          />

          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value, page: 1 })}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px' }}
          />

          <input
            type="text"
            data-testid="filtr-nip"
            placeholder="NIP kontrahenta"
            value={filters.nip}
            onChange={(e) => setFilters({ ...filters, nip: e.target.value, page: 1 })}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px', width: '140px' }}
          />

          <select
            data-testid="filtr-zrodlo"
            value={filters.zrodlo}
            onChange={(e) => setFilters({ ...filters, zrodlo: e.target.value, page: 1 })}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px' }}
          >
            <option value="">Każde źródło</option>
            <option value="ksef">Z KSeF</option>
            <option value="reczny">Ręczne</option>
            <option value="faktura_zakupowa">Z faktury zakupu</option>
          </select>

          <button
            type="button"
            data-testid="filtr-przeterminowane"
            onClick={() => setFilters({ ...filters, przeterminowane: !filters.przeterminowane, page: 1 })}
            style={{
              padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer',
              border: `1px solid ${filters.przeterminowane ? 'var(--danger)' : 'var(--border)'}`,
              backgroundColor: filters.przeterminowane ? 'var(--danger)' : 'var(--card-bg)',
              color: filters.przeterminowane ? '#fff' : 'var(--text-secondary)'
            }}
          >
            Tylko przeterminowane
          </button>
        </div>
      )}

      {/* Lista kosztow */}
      {activeTab === 'costs' && (
        <div style={{ background: 'var(--card-bg)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>Ladowanie...</div>
          ) : costs.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Brak kosztow</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Data</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Nazwa</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Kategoria</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Lokalizacja</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Kwota brutto</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Termin</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {costs.map(cost => (
                  <tr key={cost.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px' }}>{cost.data_kosztu}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: '500' }}>{cost.nazwa}</div>
                      {cost.kontrahent && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{cost.kontrahent}{cost.kontrahent_nip ? ` · NIP ${cost.kontrahent_nip}` : ''}</div>}
                      {cost.zrodlo && cost.zrodlo !== 'reczny' && ZRODLA[cost.zrodlo] && (
                        <span data-testid="zrodlo" title={ZRODLA[cost.zrodlo].opis} style={{ fontSize: '10px', fontWeight: 600, background: ZRODLA[cost.zrodlo].tlo, color: ZRODLA[cost.zrodlo].kolor, padding: '2px 6px', borderRadius: '10px', marginTop: '4px', display: 'inline-block' }}>
                          {ZRODLA[cost.zrodlo].etykieta}{cost.numer_dokumentu ? ` · ${cost.numer_dokumentu}` : ''}
                        </span>
                      )}
                      {cost.korekta && <span style={{ fontSize: '10px', fontWeight: 600, background: 'var(--orange-light)', color: 'var(--orange)', padding: '2px 6px', borderRadius: '10px', marginLeft: '4px', display: 'inline-block' }}>korekta</span>}
                      {cost.cykliczny && <span style={{ fontSize: '11px', background: 'var(--info-light)', padding: '2px 6px', borderRadius: '4px', marginTop: '4px', display: 'inline-block' }}>{cyklConfig[cost.cykl_typ]?.icon} {cyklConfig[cost.cykl_typ]?.label}</span>}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ background: cost.kategoria_kolor || 'var(--border)', color: '#fff', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                        {cost.kategoria_nazwa}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{cost.lokalizacja_nazwa || 'Firma (wszystkie)'}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(cost.kwota_brutto)}</td>
                    <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                      {cost.termin_platnosci ? formatujDate(cost.termin_platnosci) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      {cost.przeterminowany && (
                        <div data-testid="przeterminowany" style={{ fontSize: '10px', fontWeight: 600, color: 'var(--danger)' }}>
                          przeterminowany {cost.dni_po_terminie ?? dniPoTerminie(cost.termin_platnosci, dzisISO())} dni
                        </div>
                      )}
                      {(cost.forma_platnosci || cost.rachunek_nr) && (
                        <div data-testid="platnosc-ksef" title={cost.rachunek_nr ? `Rachunek z faktury: ${cost.rachunek_formatowany || formatujRachunek(cost.rachunek_nr)}` : undefined}
                          style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                          {cost.forma_platnosci || 'forma nieznana'}{cost.rachunek_nr ? <span style={{ fontFamily: 'monospace' }}> · {skrocRachunek(cost.rachunek_nr)}</span> : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{
                        background: statusConfig[cost.status]?.bg,
                        color: statusConfig[cost.status]?.text,
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {statusConfig[cost.status]?.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        {cost.status !== 'zaplacony' && (
                          <button
                            onClick={() => handlePayCost(cost)}
                            style={{ padding: '4px 10px', border: '1px solid var(--success)', color: 'var(--success)', fontSize: '11px' }}
                            title="Oznacz jako zaplacony"
                          >
                            Zaplac
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(cost)}
                          style={{ padding: '4px 10px', border: '1px solid var(--info)', color: 'var(--info)', fontSize: '11px' }}
                        >
                          Edytuj
                        </button>
                        {cost.status !== 'zaplacony' && (
                          <button
                            onClick={() => handleDeleteCost(cost)}
                            style={{ padding: '4px 10px', border: '1px solid var(--danger)', color: 'var(--danger)', fontSize: '11px' }}
                          >
                            Usun
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Paginacja */}
          {pagination.pages > 1 && (
            <div style={{ padding: '15px', display: 'flex', justifyContent: 'center', gap: '5px', borderTop: '1px solid var(--border)' }}>
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setFilters({ ...filters, page })}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: filters.page === page ? 'var(--info)' : 'var(--bg)',
                    color: filters.page === page ? '#fff' : 'var(--text)'
                  }}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Z KSeF do zaksiegowania */}
      {activeTab === 'z-ksef' && (
        <ZKsefTab sklepy={locations.map(l => ({ id: l.id, nazwa: l.nazwa }))} kategorie={categories} locationId={locationId || ''}
          onLiczba={setIleZKsef}
          onZaksiegowano={() => { fetchCosts(); fetchStats(); }} />
      )}

      {/* Podsumowanie miesieczne */}
      {activeTab === 'miesiecznie' && (
        <PodsumowanieMiesieczneTab sklepy={locations.map(l => ({ id: l.id, nazwa: l.nazwa }))} locationId={filters.location_id} />
      )}

      {/* Oznacz zaplacony (KW z kasy sklepu albo konto firmowe bez KW) */}
      {kosztDoZaplaty && (
        <OznaczZaplaconyModal koszt={kosztDoZaplaty} onZamknij={() => setKosztDoZaplaty(null)}
          onZaplacono={() => { fetchCosts(); fetchStats(); }} />
      )}

      {/* Statystyki */}
      {activeTab === 'stats' && stats && (
        <div>
          {/* Podsumowanie */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Razem kosztow</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--danger)' }}>{formatCurrency(stats.summary?.suma_brutto)}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{stats.summary?.total_costs} pozycji</div>
            </div>
            <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Do zaplaty</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--warning)' }}>{formatCurrency(stats.summary?.niezaplacone_suma)}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{stats.summary?.niezaplacone_count} pozycji</div>
            </div>
            <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Zaplacone</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--success)' }}>{formatCurrency(stats.summary?.zaplacone_suma)}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{stats.summary?.zaplacone_count} pozycji</div>
            </div>
          </div>

          {/* Per kategoria */}
          <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>Koszty per kategoria</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
              {stats.per_category?.map(cat => (
                <div key={cat.category_id} style={{ display: 'flex', alignItems: 'center', padding: '10px', background: 'var(--bg)', borderRadius: '8px', borderLeft: `4px solid ${cat.kolor}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500' }}>{cat.kategoria}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{cat.count} pozycji</div>
                  </div>
                  <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{formatCurrency(cat.suma_brutto)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Kategorie */}
      {activeTab === 'categories' && (
        <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0 }}>Kategorie kosztow</h3>
            <button
              onClick={() => setShowCategoryModal(true)}
              style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--success)', color: 'var(--success)' }}
            >
              + Dodaj kategorie
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            {categories.map(cat => (
              <div key={cat.id} style={{ padding: '15px', background: 'var(--bg)', borderRadius: '8px', borderLeft: `4px solid ${cat.kolor}` }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{cat.nazwa}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Typ: {cat.typ === 'staly' ? 'Staly' : 'Zmienny'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal dodawania/edycji kosztu */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--card-bg)', padding: '30px', borderRadius: '12px', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginTop: 0 }}>{editingCost ? 'Edytuj koszt' : 'Dodaj nowy koszt'}</h2>
            <form onSubmit={handleSaveCost}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Nazwa *</label>
                  <input
                    type="text"
                    required
                    value={formData.nazwa}
                    onChange={(e) => setFormData({ ...formData, nazwa: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                    placeholder="np. Czynsz luty 2026"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Kategoria *</label>
                  <select
                    required
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                  >
                    <option value="">Wybierz kategorie</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nazwa}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Lokalizacja</label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                  >
                    <option value="">Koszt ogolny firmy</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.nazwa}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Kwota netto *</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={formData.kwota_netto}
                    onChange={(e) => setFormData({ ...formData, kwota_netto: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Stawka VAT %</label>
                  <select
                    value={formData.stawka_vat}
                    onChange={(e) => setFormData({ ...formData, stawka_vat: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                  >
                    <option value="23">23%</option>
                    <option value="8">8%</option>
                    <option value="5">5%</option>
                    <option value="0">0%</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Data kosztu *</label>
                  <input
                    type="date"
                    required
                    value={formData.data_kosztu}
                    onChange={(e) => setFormData({ ...formData, data_kosztu: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Kontrahent</label>
                  <input
                    type="text"
                    value={formData.kontrahent}
                    onChange={(e) => setFormData({ ...formData, kontrahent: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                    placeholder="Nazwa dostawcy/wynajmujacego"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Numer dokumentu</label>
                  <input
                    type="text"
                    value={formData.numer_dokumentu}
                    onChange={(e) => setFormData({ ...formData, numer_dokumentu: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                    placeholder="np. FV/123/2026"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Okres od</label>
                  <input
                    type="date"
                    value={formData.okres_od}
                    onChange={(e) => setFormData({ ...formData, okres_od: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Okres do</label>
                  <input
                    type="date"
                    value={formData.okres_do}
                    onChange={(e) => setFormData({ ...formData, okres_do: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1', background: 'var(--bg)', padding: '15px', borderRadius: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.cykliczny}
                      onChange={(e) => setFormData({ ...formData, cykliczny: e.target.checked })}
                    />
                    <span style={{ fontWeight: '500' }}>Koszt cykliczny (powtarzalny)</span>
                  </label>

                  {formData.cykliczny && (
                    <div style={{ marginTop: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Powtarzaj co:</label>
                      <select
                        value={formData.cykl_typ}
                        onChange={(e) => setFormData({ ...formData, cykl_typ: e.target.value })}
                        style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                      >
                        <option value="">Wybierz...</option>
                        <option value="miesiac">Miesiac</option>
                        <option value="kwartal">Kwartal</option>
                        <option value="rok">Rok</option>
                      </select>
                    </div>
                  )}
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Opis</label>
                  <textarea
                    value={formData.opis}
                    onChange={(e) => setFormData({ ...formData, opis: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', minHeight: '80px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingCost(null); }}
                  style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--info)', color: 'var(--info)' }}
                >
                  {editingCost ? 'Zapisz zmiany' : 'Dodaj koszt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal kategorii */}
      {showCategoryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--card-bg)', padding: '30px', borderRadius: '12px', maxWidth: '400px', width: '90%' }}>
            <h2 style={{ marginTop: 0 }}>Nowa kategoria</h2>
            <form onSubmit={handleSaveCategory}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Nazwa</label>
                <input
                  type="text"
                  required
                  value={categoryForm.nazwa}
                  onChange={(e) => setCategoryForm({ ...categoryForm, nazwa: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Typ</label>
                <select
                  value={categoryForm.typ}
                  onChange={(e) => setCategoryForm({ ...categoryForm, typ: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                >
                  <option value="staly">Staly</option>
                  <option value="zmienny">Zmienny</option>
                </select>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Kolor</label>
                <input
                  type="color"
                  value={categoryForm.kolor}
                  onChange={(e) => setCategoryForm({ ...categoryForm, kolor: e.target.value })}
                  style={{ width: '100%', height: '40px', borderRadius: '6px', border: '1px solid var(--border)' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCategoryModal(false)} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}>Anuluj</button>
                <button type="submit" style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--success)', color: 'var(--success)' }}>Dodaj</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
